// eslint-disable-next-line no-restricted-imports
import { useQuery, UseQueryOptions, QueryKey } from '@tanstack/react-query';
import { Select, SelectProps, Spin } from 'antd';
import { BaseSelectRef } from 'rc-select';
import React, { useState, ReactElement, useMemo, useEffect } from 'react';
import styled from 'styled-components';
import { t } from 'ttag';
import { useDebouncedCallback } from 'use-debounce';

import NotFoundContent from '../NotFoundContent';

const Box = styled.div`
  text-align: center;
  margin: 1em;
`;

const dropdownRender = (menu: ReactElement) => {
  if (menu?.props?.searchValue === '') {
    return <NotFoundContent description={t`Please type to search`} />;
  }
  return menu;
};

// We forward any unknown prop to the Select, except for these, which we explicitly override
type ForwardedSelectProps = Omit<
  SelectProps,
  | 'showSearch'
  | 'onSearch'
  | 'filterOption'
  | 'loading'
  | 'notFoundContent'
  | 'dropdownRender'
  | 'options'
>;

type SearchQuerySelectProps<TData = any, TQueryKey extends QueryKey = QueryKey> = {
  // forwardRef does not support default props because the function isn't actually
  // a component. We default the argument, but ESLint can't detect it correctly
  // https://github.com/facebook/react/issues/16653#issuecomment-557603361
  // eslint-disable-next-line react/require-default-props
  debounceMs?: number;

  /**
   * A function that returns React Query options based on the search term.
   * This function will be called with the current search value and should return
   * the query options including queryKey and queryFn.
   */
  getQueryOptions: (searchTerm: string) => UseQueryOptions<TData, unknown, TData, TQueryKey>;

  /**
   * Optional function to transform the query data into options for the Select component.
   * If not provided, the raw data will be passed as children.
   */
  options: (data: TData) => SelectProps['options'];
};

type Props<TData = any, TQueryKey extends QueryKey = QueryKey> = SearchQuerySelectProps<
  TData,
  TQueryKey
> &
  ForwardedSelectProps;

const SearchQuerySelect = React.forwardRef<BaseSelectRef, Props>(
  <TData = any, TQueryKey extends QueryKey = QueryKey>(
    {
      debounceMs = 500,
      value,
      onChange,
      getQueryOptions,
      options,
      ...rest
    }: Props<TData, TQueryKey>,
    ref: React.Ref<BaseSelectRef>,
  ) => {
    const [searchTerm, setSearchTerm] = useState('');

    const debouncedSetSearch = useDebouncedCallback(
      (search: string) => setSearchTerm(search),
      debounceMs,
    );

    // Cleanup debounced callback on unmount
    useEffect(
      () => () => {
        debouncedSetSearch.cancel();
      },
      [debouncedSetSearch],
    );

    // Get query options based on the debounced search term
    const queryOptions = useMemo(() => getQueryOptions(searchTerm), [searchTerm, getQueryOptions]);

    // Use React Query to fetch data
    const { data, isLoading, isFetching } = useQuery({
      ...queryOptions,
      enabled: !!queryOptions,
    });

    // Determine if we're currently fetching
    const fetching = isLoading || isFetching;

    return (
      <Select
        {...rest}
        showSearch
        ref={ref}
        value={value}
        onChange={onChange}
        filterOption={false}
        onSearch={debouncedSetSearch}
        loading={fetching}
        notFoundContent={
          fetching ? (
            <Box>
              <Spin size="small" />
            </Box>
          ) : (
            <NotFoundContent />
          )
        }
        dropdownRender={dropdownRender}
        options={options(data)}
      />
    );
  },
);

export default SearchQuerySelect;
